import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import SEO from "../components/SEO";
import { toast } from "sonner";

export default function SoumettreEvenement() {
    const navigate = useNavigate();
    const [cities, setCities] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({
        submitter_name: "",
        submitter_email: "",
        submitter_phone: "",
        submitter_org: "",
        title: "",
        description: "",
        category: "culture",
        start_date: "",
        end_date: "",
        start_time: "",
        venue: "",
        city: "",
        address: "",
        price_info: "",
        website: "",
        image_url: "",
        consent: false,
    });

    useEffect(() => {
        api.get("/event-submissions/territory").then(r => setCities(r.data?.cities || [])).catch(() => {});
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error("Merci de cocher la case d'accord pour publication.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form };
            // Strip empty optional fields so HttpUrl/EmailStr validation passes
            ["submitter_phone", "submitter_org", "end_date", "start_time", "address", "price_info", "website", "image_url"].forEach(k => {
                if (!payload[k]) delete payload[k];
            });
            await api.post("/events-submit", payload);
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Une erreur est survenue. Réessayez.";
            toast.error(typeof detail === "string" ? detail : "Validation échouée — vérifiez les champs.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <>
                <SEO
                    title="Proposition reçue"
                    description="Votre proposition d'événement est arrivée à la rédaction de Côte Perdue."
                    path="/soumettre-evenement"
                    noindex
                />
                <div className="min-h-screen pt-32 md:pt-40 pb-32 px-6 md:px-12 bg-cp-craie">
                    <div className="max-w-2xl mx-auto text-center">
                        <p className="cp-tiny text-cp-foret mb-6">Merci.</p>
                        <h1 className="cp-serif text-5xl md:text-6xl text-cp-encre leading-[0.95] mb-8">
                            Bien <em className="cp-display-italic text-cp-foret">reçu.</em>
                        </h1>
                        <p className="cp-serif text-xl italic font-light text-cp-gris leading-relaxed mb-12">
                            La rédaction lit chaque proposition.<br />
                            Vous recevrez une réponse <em>sous cinq jours</em>, à l'adresse que vous avez indiquée.
                        </p>
                        <button
                            onClick={() => navigate("/")}
                            data-testid="back-home-after-submit"
                            className="cp-btn-primary"
                        >
                            Retour à l'accueil
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO
                title="Soumettre un événement"
                description="Vous organisez un concert, une expo, une brocante ou un atelier sur la côte de Normandie ou de Picardie ? Proposez votre événement à la rédaction de Côte Perdue."
                path="/soumettre-evenement"
            />
            <div className="min-h-screen pt-32 md:pt-40 pb-24 bg-cp-ivoire-bright">
                {/* HERO */}
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 mb-16">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-7">
                            <p className="cp-tiny text-cp-foret mb-6">Soumettre un événement</p>
                            <h1 className="cp-serif text-5xl md:text-6xl lg:text-7xl text-cp-encre leading-[0.95] mb-8">
                                Faites <em className="cp-display-italic text-cp-foret">vivre</em><br />
                                la côte.
                            </h1>
                            <p className="cp-serif text-xl italic font-light text-cp-gris leading-relaxed max-w-xl">
                                Concert, exposition, brocante, atelier, fête locale — si votre événement se tient sur l'une des seize communes de notre territoire, proposez-le-nous.
                            </p>
                        </div>
                        <div className="md:col-span-4 md:col-start-9 self-end">
                            <p className="cp-mono text-sm text-cp-gris leading-relaxed">
                                Chaque proposition est lue par la rédaction. Si elle correspond à la ligne éditoriale, elle est mise en ligne sous cinq jours. Aucun frais. Aucune contrepartie.
                            </p>
                        </div>
                    </div>
                </div>

                {/* FORM */}
                <form onSubmit={onSubmit} className="max-w-3xl mx-auto px-6 md:px-12 space-y-10" data-testid="event-submit-form">
                    {/* SECTION : QUI ÊTES-VOUS */}
                    <Section number="I." label="Qui êtes-vous ?">
                        <Field2>
                            <Input testid="ev-submitter-name" required label="Votre nom *" value={form.submitter_name} onChange={set("submitter_name")} />
                            <Input testid="ev-submitter-email" required type="email" label="Votre email *" value={form.submitter_email} onChange={set("submitter_email")} />
                        </Field2>
                        <Field2>
                            <Input testid="ev-submitter-phone" label="Téléphone (facultatif)" value={form.submitter_phone} onChange={set("submitter_phone")} />
                            <Input testid="ev-submitter-org" label="Structure / lieu (facultatif)" value={form.submitter_org} onChange={set("submitter_org")} />
                        </Field2>
                    </Section>

                    {/* SECTION : L'ÉVÉNEMENT */}
                    <Section number="II." label="L'événement">
                        <Input testid="ev-title" required label="Titre *" value={form.title} onChange={set("title")} placeholder="Ex : Brocante de la rue Mauconseil" />
                        <Textarea testid="ev-description" required label="Description *" value={form.description} onChange={set("description")} placeholder="Décrivez l'événement en quelques lignes (programme, ambiance, public, billetterie…). Minimum 20 caractères." rows={6} />
                        <Field2>
                            <Select testid="ev-category" label="Catégorie" value={form.category} onChange={set("category")}>
                                <option value="culture">Culture · spectacle</option>
                                <option value="concert">Concert · musique</option>
                                <option value="expo">Exposition</option>
                                <option value="brocante">Brocante · vide-greniers</option>
                                <option value="marche">Marché · fête locale</option>
                                <option value="atelier">Atelier · cours</option>
                                <option value="autre">Autre</option>
                            </Select>
                            <Input testid="ev-price" label="Tarif (texte libre)" value={form.price_info} onChange={set("price_info")} placeholder="Ex : Gratuit, ou 12€ / 8€ réduit" />
                        </Field2>
                    </Section>

                    {/* SECTION : QUAND */}
                    <Section number="III." label="Quand">
                        <Field2>
                            <Input testid="ev-start-date" required type="date" label="Date de début *" value={form.start_date} onChange={set("start_date")} />
                            <Input testid="ev-end-date" type="date" label="Date de fin (si plusieurs jours)" value={form.end_date} onChange={set("end_date")} />
                        </Field2>
                        <Input testid="ev-start-time" type="time" label="Heure de début (facultatif)" value={form.start_time} onChange={set("start_time")} />
                    </Section>

                    {/* SECTION : OÙ */}
                    <Section number="IV." label="Où">
                        <Input testid="ev-venue" required label="Nom du lieu *" value={form.venue} onChange={set("venue")} placeholder="Ex : Salle des Fêtes, Église Saint-Jacques, Plage du Casino…" />
                        <Field2>
                            <Select testid="ev-city" required label="Commune *" value={form.city} onChange={set("city")}>
                                <option value="">— Choisir —</option>
                                {cities.map(c => <option key={c} value={c}>{c}</option>)}
                            </Select>
                            <Input testid="ev-address" label="Adresse (rue, n°)" value={form.address} onChange={set("address")} />
                        </Field2>
                    </Section>

                    {/* SECTION : LIENS */}
                    <Section number="V." label="Liens & visuel">
                        <Input testid="ev-website" type="url" label="Site web ou page billetterie (facultatif)" value={form.website} onChange={set("website")} placeholder="https://…" />
                        <Input testid="ev-image" type="url" label="Photo (URL publique — Imgur, Wikimedia, votre site, etc.)" value={form.image_url} onChange={set("image_url")} placeholder="https://…/photo.jpg" />
                        <p className="cp-tiny text-cp-gris mt-2 leading-relaxed">
                            Pour la photo, hébergez-la sur un service public (Imgur, Wikimedia Commons, votre propre site) et collez l'URL directe ici. Aucun upload.
                        </p>
                    </Section>

                    {/* CONSENT */}
                    <div className="border-t border-cp-border pt-8">
                        <label className="flex items-start gap-3 cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={form.consent}
                                onChange={set("consent")}
                                className="mt-1.5 accent-cp-foret w-4 h-4"
                                data-testid="ev-consent"
                            />
                            <span className="cp-serif text-base text-cp-encre leading-relaxed">
                                J'autorise <em className="cp-display-italic">Côte Perdue</em> à publier l'événement et les informations ci-dessus si la rédaction valide la proposition. Je certifie avoir le droit de communiquer ces informations.
                            </span>
                        </label>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center pt-4">
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="ev-submit-button"
                            className="cp-btn-primary disabled:opacity-50"
                        >
                            {submitting ? "Envoi en cours…" : "Envoyer la proposition"}
                        </button>
                        <p className="cp-tiny text-cp-gris">Vous recevrez une réponse sous cinq jours.</p>
                    </div>
                </form>
            </div>
        </>
    );
}

// ------- Form atoms -------
function Section({ number, label, children }) {
    return (
        <div className="border-t border-cp-border pt-8">
            <div className="flex items-baseline gap-4 mb-6">
                <span className="cp-serif text-2xl italic text-cp-sable-deep">{number}</span>
                <h2 className="cp-serif text-2xl text-cp-encre">{label}</h2>
            </div>
            <div className="space-y-5">{children}</div>
        </div>
    );
}
function Field2({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>; }
function Input({ testid, label, ...props }) {
    return (
        <label className="block">
            <span className="cp-tiny text-cp-gris mb-2 block">{label}</span>
            <input {...props} data-testid={testid} className="w-full px-4 py-3 bg-cp-ivoire border border-cp-border focus:border-cp-foret focus:outline-none cp-serif text-base text-cp-encre transition-colors" />
        </label>
    );
}
function Textarea({ testid, label, ...props }) {
    return (
        <label className="block">
            <span className="cp-tiny text-cp-gris mb-2 block">{label}</span>
            <textarea {...props} data-testid={testid} className="w-full px-4 py-3 bg-cp-ivoire border border-cp-border focus:border-cp-foret focus:outline-none cp-serif text-base text-cp-encre transition-colors leading-relaxed resize-y" />
        </label>
    );
}
function Select({ testid, label, children, ...props }) {
    return (
        <label className="block">
            <span className="cp-tiny text-cp-gris mb-2 block">{label}</span>
            <select {...props} data-testid={testid} className="w-full px-4 py-3 bg-cp-ivoire border border-cp-border focus:border-cp-foret focus:outline-none cp-serif text-base text-cp-encre transition-colors">
                {children}
            </select>
        </label>
    );
}
